import { Button, Checkbox, SegmentedControl, Select, TextInput } from '@mantine/core';
import {
  IconClipboardList,
  IconFileTypePdf,
  IconFileZip,
  IconPlayerPlay,
  IconSearch,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { recordPath } from '../forms/record-model.js';
import { errorCode } from '../personnel/source.js';
import table from '../pms/Pms.module.css';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import { LoadError, TablePager, TableSkeleton } from '../purchasing/table.js';
import {
  ARCHIVE_EXPORT_MAX,
  type ArchiveFilter,
  type ArchiveRow,
  archiveRows,
  selectableIds,
} from './archive-view.js';
import classes from './DrillPlanner.module.css';
import { StartRecordDialog } from './DrillRecordDialogs.js';
import records from './DrillRecords.module.css';
import { DrillRecordWizard, RecordStateBadge } from './DrillRecordWizard.js';
import type { DrillRecordSource } from './record-source.js';
import type { DrillLegacyRecord, DrillRecordListItem, DrillRecords } from './record-types.js';
import {
  RECORD_FILTERS,
  type RecordFilter,
  WIZARD_STAGES,
  type WizardStage,
} from './record-view.js';
import { useDrillReportExport } from './report-export.js';

export interface DrillRecordsScreenProps {
  source: DrillRecordSource;
}

/**
 * Adres: `/operations/drills/records?record=<id>&stage=<aşama>`; `?start=<planId>` başlatır.
 * Arşiv süzgeci: `status`, `type` (ana kategori), `from`, `to` (`YYYY-MM-DD`), ofiste `vessel`.
 */
export const DRILL_RECORDS_PATH = '/operations/drills/records';

const PAGE_SIZE = 15;

const isDay = (value: string | null) => (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null);

/**
 * Operations ▸ Drills ▸ Records (ortağın belgesi §5 ve §8; ADR 0128 Faz 3 ve Faz 5 Ek): geminin
 * tatbikat kayıtları ve Execution Wizard; arşiv olarak tarih, tür, durum ve aramayla süzülür,
 * seçili kayıtlar tek PDF ya da manifestli ZIP olarak dışa aktarılır. Eski ISM tatbikat formları
 * "Legacy" satırıyla görünür (taşınmaz). Gemide sorumlu zabit "Conduct Drill" ile başlatır,
 * kaptan karar verir; ofiste filo kayıtları salt okunur. Seçili kayıt ve aşama adreste.
 */
export function DrillRecordsScreen({ source }: DrillRecordsScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillRecord.list.${key}`, values);
  const a = (key: string) => t(`drillReport.archive.${key}`);
  const shell = useShell();
  const [query, setQuery] = usePmsQuery();
  const vesselId = source.scope === 'office' ? query.get('vessel') : null;
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: DrillRecords }
  >({ kind: 'loading' });
  const [page, setPage] = useState(1);
  const [starting, setStarting] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', data: await source.list(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const recordId = query.get('record');
  const startPlan = query.get('start');
  const stage: WizardStage = WIZARD_STAGES.find((s) => s === query.get('stage')) ?? 'general';
  const filter: RecordFilter = RECORD_FILTERS.find((f) => f === query.get('status')) ?? 'all';
  const from = isDay(query.get('from'));
  const to = isDay(query.get('to'));
  const categoryId = query.get('type');
  const filters: ArchiveFilter = useMemo(
    () => ({ status: filter, from, to, categoryId, search }),
    [filter, from, to, categoryId, search],
  );
  const data = state.kind === 'ready' ? state.data : null;
  const canStart =
    data?.side === 'ship' && data.can.create && Boolean(source.start) && data.vessels.length > 0;
  // Planner'dan "Conduct Drill" (`?start=<planId>`): süren kaydı varsa onu açar
  useEffect(() => {
    if (!data || !startPlan) return;
    const plan = data.plans.find((p) => p.id === startPlan);
    if (plan?.openRecordId) setQuery({ start: null, record: plan.openRecordId, stage: null });
    else if (canStart) setStarting(true);
  }, [data, startPlan, canStart, setQuery]);

  if (recordId) {
    return (
      <div className={table.screen}>
        <DrillRecordWizard
          key={recordId}
          id={recordId}
          source={source}
          stage={stage}
          onStage={(next) => setQuery({ stage: next === 'general' ? null : next })}
          onBack={() => {
            setQuery({ record: null, stage: null });
            void load();
          }}
        />
      </div>
    );
  }

  const header = (
    <PageHeader
      title={t('menu.operationsDrillsRecords')}
      description={k(source.scope === 'office' ? 'officeDescription' : 'description')}
      actions={
        canStart ? (
          <Button
            leftSection={<IconPlayerPlay size={16} aria-hidden />}
            onClick={() => setStarting(true)}
          >
            {t('drillRecord.actions.conduct')}
          </Button>
        ) : undefined
      }
    />
  );
  if (state.kind === 'loading') {
    return (
      <div className={table.screen}>
        {header}
        <TableSkeleton columns={8} label={t('menu.operationsDrillsRecords')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={table.screen}>
        {header}
        <Panel>
          <LoadError code={state.code} title={k('error')} onRetry={() => void load()} />
        </Panel>
      </div>
    );
  }
  const ready = state.data;
  const rows = archiveRows(ready.records, ready.legacy ?? [], filters);
  const categories = ready.categories ?? [];
  const patch = (values: Record<string, string | null>) => {
    setPage(1);
    setQuery(values);
  };
  return (
    <div className={table.screen}>
      {header}
      <Panel>
        <div className={classes.bar}>
          <SegmentedControl
            aria-label={k('filter')}
            value={filter}
            onChange={(value) => patch({ status: value === 'all' ? null : value })}
            data={RECORD_FILTERS.map((f) => ({ value: f, label: k(`filters.${f}`) }))}
          />
          {source.scope === 'office' ? (
            <Select
              aria-label={k('vessel')}
              placeholder={k('allVessels')}
              clearable
              data={ready.vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={vesselId}
              onChange={(value) => setQuery({ vessel: value })}
            />
          ) : null}
        </div>
        <div className={records.archiveBar}>
          <TextInput
            className={records.archiveSearch}
            aria-label={a('search')}
            placeholder={a('searchPlaceholder')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={search}
            onChange={(event) => {
              setPage(1);
              setSearch(event.currentTarget.value);
            }}
          />
          {categories.length > 0 ? (
            <Select
              aria-label={a('type')}
              placeholder={a('allTypes')}
              clearable
              data={categories.map((c) => ({ value: c.id, label: c.name }))}
              value={categoryId}
              onChange={(value) => patch({ type: value })}
            />
          ) : null}
          <DayInput
            aria-label={a('from')}
            placeholder={a('from')}
            clearable
            value={from}
            onChange={(value) => patch({ from: value })}
          />
          <DayInput
            aria-label={a('to')}
            placeholder={a('to')}
            clearable
            value={to}
            onChange={(value) => patch({ to: value })}
          />
        </div>
        <ArchiveExportBar
          source={source}
          rows={rows}
          selected={selected}
          onClear={() => setSelected(new Set())}
        />
        <RecordsTable
          rows={rows}
          total={ready.records.length + (ready.legacy?.length ?? 0)}
          fleet={ready.side === 'office' && vesselId === null}
          page={page}
          onPage={setPage}
          onOpen={(row) => setQuery({ record: row.id, stage: null })}
          onOpenLegacy={(row) => shell?.navigate(recordPath(row.id))}
          selected={selected}
          onSelect={(ids, on) =>
            setSelected((current) => {
              const next = new Set(current);
              for (const id of ids) {
                if (on) next.add(id);
                else next.delete(id);
              }
              return next;
            })
          }
        />
      </Panel>
      <StartRecordDialog
        opened={starting}
        data={ready}
        planId={startPlan}
        source={source}
        onClose={() => {
          setStarting(false);
          if (startPlan) setQuery({ start: null });
        }}
        onStarted={(record) => {
          setStarting(false);
          setQuery({ start: null, record: record.id, stage: null });
        }}
      />
    </div>
  );
}

/**
 * Seçili kayıtların toplu dışa aktarımı (Faz 5 Ek): tek PDF ya da kayıt başına PDF ve SHA-256
 * manifestli ZIP. Seçim görünen kayıtlarla sınırlıdır; en çok {@link ARCHIVE_EXPORT_MAX} kayıt.
 */
function ArchiveExportBar({
  source,
  rows,
  selected,
  onClear,
}: {
  source: DrillRecordSource;
  rows: ArchiveRow[];
  selected: ReadonlySet<string>;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const a = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillReport.archive.${key}`, values);
  const { busy, bulk } = useDrillReportExport(source);
  const [message, setMessage] = useState<{ tone: 'error' | 'ok'; text: string } | null>(null);
  const ids = selectableIds(rows).filter((id) => selected.has(id));
  if (ids.length === 0 && !message && !busy) return null;
  const tooMany = ids.length > ARCHIVE_EXPORT_MAX;
  const run = async (format: 'pdf' | 'zip') => {
    setMessage(null);
    const error = await bulk(ids, format, `drill-records-${new Date().toISOString().slice(0, 10)}`);
    setMessage(
      error
        ? { tone: 'error', text: error }
        : { tone: 'ok', text: a('exported', { count: ids.length }) },
    );
  };
  return (
    <fieldset className={records.exportBar}>
      <legend className={records.exportCount} aria-live="polite">
        {busy
          ? a('preparing', { done: busy.done, total: busy.total })
          : ids.length > 0
            ? a('selected', { count: ids.length })
            : null}
      </legend>
      {ids.length > 0 ? (
        <>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconFileTypePdf size={16} aria-hidden />}
            title={t('drillReport.actions.exportPdfHint')}
            disabled={tooMany || busy !== null}
            onClick={() => void run('pdf')}
          >
            {t('drillReport.actions.exportPdf')}
          </Button>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconFileZip size={16} aria-hidden />}
            title={t('drillReport.actions.exportZipHint')}
            disabled={tooMany || busy !== null}
            onClick={() => void run('zip')}
          >
            {t('drillReport.actions.exportZip')}
          </Button>
          <Button size="xs" variant="subtle" disabled={busy !== null} onClick={onClear}>
            {t('drillReport.actions.clear')}
          </Button>
        </>
      ) : null}
      {tooMany ? (
        <span className={records.exportError}>{a('limit', { max: ARCHIVE_EXPORT_MAX })}</span>
      ) : null}
      {message ? (
        <span
          className={message.tone === 'error' ? records.exportError : records.exportOk}
          role={message.tone === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </span>
      ) : null}
    </fieldset>
  );
}

const LEGACY_TONE: Record<string, StatusTone> = {
  pending: 'info',
  returned: 'danger',
  approved: 'success',
  void: 'neutral',
  inconsistent: 'warning',
};

/**
 * Kayıt listesi: seçim, numara, tatbikat, tarih, sorumlu, katılım, adımlar, durum. Eski ISM
 * tatbikat formları "Legacy (ISM form)" rozetiyle aynı tabloda; seçilemez, asıl form kaydını açar.
 */
export function RecordsTable({
  rows,
  total,
  fleet,
  page,
  onPage,
  onOpen,
  onOpenLegacy,
  selected,
  onSelect,
}: {
  rows: ArchiveRow[];
  total: number;
  fleet: boolean;
  page: number;
  onPage: (page: number) => void;
  onOpen: (row: DrillRecordListItem) => void;
  onOpenLegacy?: (row: DrillLegacyRecord) => void;
  selected?: ReadonlySet<string>;
  onSelect?: (ids: string[], on: boolean) => void;
}) {
  const { t } = useTranslation();
  const c = (key: string) => t(`drillRecord.columns.${key}`);
  const a = (key: string) => t(`drillReport.archive.${key}`);
  if (total === 0 || rows.length === 0) {
    return (
      <EmptyState
        icon={<IconClipboardList size={22} />}
        title={t(`drillRecord.list.${total === 0 ? 'empty' : 'noMatch'}`)}
      />
    );
  }
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const shown = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const selectable = selected && onSelect ? selectableIds(shown) : null;
  const allOn =
    selectable !== null && selectable.length > 0 && selectable.every((id) => selected?.has(id));
  const someOn = selectable?.some((id) => selected?.has(id)) ?? false;
  return (
    <>
      <section
        className={table.tableBox}
        aria-label={t('menu.operationsDrillsRecords')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={table.table} data-cards>
          <thead>
            <tr>
              {selectable ? (
                <th scope="col" className={records.selectCell}>
                  <Checkbox
                    aria-label={t('drillReport.actions.selectAll')}
                    checked={allOn}
                    indeterminate={someOn && !allOn}
                    disabled={selectable.length === 0}
                    onChange={() => onSelect?.(selectable, !allOn)}
                  />
                </th>
              ) : null}
              <th scope="col">{c('recordNo')}</th>
              {fleet ? <th scope="col">{c('vessel')}</th> : null}
              <th scope="col">{c('drill')}</th>
              <th scope="col">{c('date')}</th>
              <th scope="col">{c('responsible')}</th>
              <th scope="col">{c('participants')}</th>
              <th scope="col">{c('steps')}</th>
              <th scope="col">{c('status')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((item) => {
              if (item.kind === 'legacy') {
                const row = item.legacy;
                return (
                  <tr key={`legacy-${row.id}`} data-legacy>
                    {selectable ? <td className={records.selectCell} /> : null}
                    <td data-label={c('recordNo')}>
                      {onOpenLegacy ? (
                        <button
                          type="button"
                          className={classes.linkButton}
                          onClick={() => onOpenLegacy(row)}
                        >
                          {row.recordNo}
                        </button>
                      ) : (
                        row.recordNo
                      )}
                    </td>
                    {fleet ? <td data-label={c('vessel')}>{row.vesselName ?? '—'}</td> : null}
                    <td data-label={c('drill')} className={classes.wrapCell}>
                      <span className={classes.drill}>
                        <span className={classes.drillCode}>{row.docNo}</span>
                        <span className={classes.drillName}>{row.title ?? row.documentTitle}</span>
                        <span className={records.badges} title={a('legacyHint')}>
                          <StatusBadge tone="neutral">{a('legacy')}</StatusBadge>
                        </span>
                      </span>
                    </td>
                    <td data-label={c('date')}>{formatDay(row.recordDay)}</td>
                    <td data-label={c('responsible')}>—</td>
                    <td data-label={c('participants')}>—</td>
                    <td data-label={c('steps')}>—</td>
                    <td data-label={c('status')}>
                      <StatusBadge tone={LEGACY_TONE[row.status] ?? 'neutral'}>
                        {t(`drillReport.archive.legacyStatus.${row.status}`, {
                          defaultValue: row.status,
                        })}
                      </StatusBadge>
                    </td>
                  </tr>
                );
              }
              const row = item.record;
              return (
                <tr key={row.id}>
                  {selectable ? (
                    <td className={records.selectCell}>
                      <Checkbox
                        aria-label={t('drillReport.actions.select', { no: row.recordNo })}
                        checked={selected?.has(row.id) ?? false}
                        onChange={(event) => onSelect?.([row.id], event.currentTarget.checked)}
                      />
                    </td>
                  ) : null}
                  <td data-label={c('recordNo')}>
                    <button
                      type="button"
                      className={classes.linkButton}
                      onClick={() => onOpen(row)}
                    >
                      {row.recordNo}
                    </button>
                  </td>
                  {fleet ? <td data-label={c('vessel')}>{row.vesselName ?? '—'}</td> : null}
                  <td data-label={c('drill')} className={classes.wrapCell}>
                    <span className={classes.drill}>
                      <span className={classes.drillCode}>{row.templateCode}</span>
                      <span className={classes.drillName}>{row.templateName}</span>
                    </span>
                  </td>
                  <td data-label={c('date')}>{formatDay(row.drillDate)}</td>
                  <td data-label={c('responsible')}>{row.responsibleName ?? '—'}</td>
                  <td data-label={c('participants')}>{`${row.present} / ${row.participants}`}</td>
                  <td data-label={c('steps')}>{`${row.stepsDone} / ${row.steps}`}</td>
                  <td data-label={c('status')}>
                    <span className={records.badges}>
                      <RecordStateBadge
                        state={row.state}
                        returned={row.returned}
                        reopened={row.reopened}
                      />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      {rows.length > PAGE_SIZE ? (
        <TablePager
          page={current}
          pages={pages}
          summary={t('drills.showing', {
            from: (current - 1) * PAGE_SIZE + 1,
            to: Math.min(current * PAGE_SIZE, rows.length),
            total: rows.length,
          })}
          label={t('menu.operationsDrillsRecords')}
          onPage={onPage}
        />
      ) : null}
    </>
  );
}
